// import hooks
import { useEffect, useMemo } from 'react';
import { useGetCLAOptions } from 'queries';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// import types
import { CLA_SETTINGS_FLAG, TabProps } from '../CLASettings.types';

// import components
import Grid from '@mui/material/Grid';
import withReadModeField from 'components/shared/CRUD/components/InnerPage/components/withReadModeField';
import {
  ControlledRadioGroup,
  ControlledTextField
} from 'components/shared/ControlledFields';
import ControlledNumericField from 'components/shared/ControlledFields/ControlledNumericField';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import CLASelect from '../../../../shared/CLASelect/CLASelect';
import ControlledCheckbox from '../../../../shared/ControlledFields/ControlledCheckbox';
import { GridInCustomSizes } from 'components/shared/Grid/Grid.components';

// import utils
import { generalSchema } from '../CLASettings.schema';
import { DefaultReadModeComponent } from 'components/shared/CRUD/components/InnerPage/components/withReadModeFieldRenderers';

const ControlledTextFieldWithReadMode = withReadModeField(ControlledTextField);
const ControlledNumericFieldWithReadMode = withReadModeField(
  ControlledNumericField
);
const CLASelectWithReadMode = withReadModeField(CLASelect);

const GeneralTab = ({ setCurrentSchema, isEditable }: TabProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'adminPortal.cla' });

  const memoizedVariant = useMemo(() => {
    return isEditable ? 'outlined' : 'standard';
  }, [isEditable]);

  const { data: claOptions } = useGetCLAOptions();

  const { watch, control, setValue } = useFormContext();

  const claId = watch('claId');
  const claCode = watch('code');

  useEffect(() => {
    setCurrentSchema(generalSchema(t));
  }, [claId]);

  return (
    <div style={{ paddingBottom: 60 }}>
      <MultiStepForm.Section title={t('general')}>
        <Grid
          container
          spacing={{ compact: 2, medium: 3 }}
          columns={{ compact: 4, medium: 8, large: 12 }}
        >
          <GridInCustomSizes>
            <CLASelectWithReadMode
              label={t('code')}
              name="claId"
              readModeRenderer={(props) => (
                <DefaultReadModeComponent {...props} value={claCode ?? ''} />
              )}
              onChange={(claId: string | number) => {
                setValue(
                  'description',
                  claOptions?.find((option) => option.claId === claId)
                    ?.claSvc ?? ''
                );
                setValue(
                  'claExternalCode',
                  claOptions?.find((option) => option.claId === claId)
                    ?.cla_ext ?? ''
                );
              }}
            />
          </GridInCustomSizes>
          <GridInCustomSizes>
            <ControlledNumericFieldWithReadMode
              name="year"
              variant="outlined"
              label={t('year')}
              step={1}
              minValue={0}
            />
          </GridInCustomSizes>

          <GridInCustomSizes>
            <ControlledTextFieldWithReadMode
              disabled={true}
              control={control}
              name="description"
              label={t('description')}
              variant="outlined"
            />
          </GridInCustomSizes>
          <GridInCustomSizes>
            <ControlledTextFieldWithReadMode
              disabled={true}
              control={control}
              name="claExternalCode"
              variant="outlined"
              label={t('externalCode')}
            />
          </GridInCustomSizes>
        </Grid>
      </MultiStepForm.Section>
      <MultiStepForm.Section title={t('yearHoursSettings')}>
        <Grid
          container
          spacing={{ compact: 2, medium: 3 }}
          columns={{ compact: 4, medium: 8, large: 12 }}
        >
          <GridInCustomSizes>
            <ControlledCheckbox
              disabled={!isEditable}
              control={control}
              name="workHoursBasedWeekdays"
              label={t('workHoursBasedWeekdays')}
            />
          </GridInCustomSizes>
          <GridInCustomSizes>
            <ControlledCheckbox
              disabled={!isEditable}
              control={control}
              name={`flags[${CLA_SETTINGS_FLAG.LEAVE_HOURS_EQUAL_PERIODS}]`}
              label={t('leaveEqualPeriods')}
            />
          </GridInCustomSizes>
          <GridInCustomSizes>
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              name="hoursWorkingPerYear"
              variant={memoizedVariant}
              label={t('yearHours')}
              step={0.2}
              minValue={0}
            />
          </GridInCustomSizes>
          <GridInCustomSizes>
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              name="periodHours"
              variant={memoizedVariant}
              label={t('periodHours')}
              step={0.2}
              minValue={0}
            />
          </GridInCustomSizes>
          <GridInCustomSizes>
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              name="hoursWorkingPerWeek"
              variant={memoizedVariant}
              label={t('weekHours')}
              step={0.2}
              minValue={0}
            />
          </GridInCustomSizes>
          <GridInCustomSizes>
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              name="hoursWorkingPerDay"
              variant={memoizedVariant}
              label={t('dayHours')}
              step={0.2}
              minValue={0}
            />
          </GridInCustomSizes>
          <GridInCustomSizes>
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              name="hoursHolidayPerYear"
              variant={memoizedVariant}
              label={t('leave')}
              step={0.2}
              minValue={0}
            />
          </GridInCustomSizes>
          <GridInCustomSizes>
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              name="hoursPlbYear"
              variant={memoizedVariant}
              label={t('hoursPlbYear')}
              step={0.2}
              minValue={0}
            />
          </GridInCustomSizes>
          <GridInCustomSizes>
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              name="hoursHolidayYearMinimum"
              variant={memoizedVariant}
              label={t('minLeave')}
              step={0.2}
              minValue={0}
            />
          </GridInCustomSizes>
        </Grid>
      </MultiStepForm.Section>
      <MultiStepForm.Section title={t('PLBSettings')}>
        <Grid
          container
          spacing={{ compact: 2, medium: 3 }}
          columns={{ compact: 4, medium: 8, large: 12 }}
        >
          <GridInCustomSizes>
            <ControlledCheckbox
              disabled={!isEditable}
              control={control}
              name={`flags[${CLA_SETTINGS_FLAG.PLB_VISIBILITY}]`}
              label={t('PLBHourManagement')}
            />
          </GridInCustomSizes>
          <GridInCustomSizes>
            <ControlledTextFieldWithReadMode
              disabled={!isEditable}
              control={control}
              name="plbName"
              label={t('PLBName')}
              variant={memoizedVariant}
            />
          </GridInCustomSizes>
          <GridInCustomSizes>
            <ControlledRadioGroup
              disabled={!isEditable}
              direction="row"
              control={control}
              name="plbValidity"
              label={t('validity')}
              options={[
                {
                  value: CLA_SETTINGS_FLAG.PLB_5_YEARS,
                  label: t('5Year')
                },
                {
                  value: CLA_SETTINGS_FLAG.PLB_10_YEARS,
                  label: t('10Year')
                }
              ]}
            />
          </GridInCustomSizes>
        </Grid>
      </MultiStepForm.Section>
    </div>
  );
};

export default GeneralTab;
